import React, { useState, useEffect, useContext } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { getAllUserApi } from "../../api/user";
import { getProjectsDataApi } from "../../api/project";
import { useUserRole } from "../../hook/useUserRole.js";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import MainTable from "../../component/table/MainTable.js";
import SelectComponent from "../../component/SelectComponent.js";
import FullScreenLoading from "../../component/FullScreenLoading.js";
import CreateUserPopup from "./components/CreateUserPopup.js";
import { useUser } from "../../hook/useUser.js";

const tableStyle = {
  height: "calc(100% - 52px)",
  maxHeight: "calc(100% - 52px)",
  ".MuiButtonBase-root.MuiTableSortLabel-root.Mui-active": {
    color: "#fff",
  },
  ".MuiButtonBase-root.MuiTableSortLabel-root:hover": {
    color: "#fff",
  },
  ".MuiTableSortLabel-icon": {
    color: "#fff!important",
    opacity: "1!important",
  },
};
const buttonStyle = {
  pr: 1.5,
  pl: 1,
  py: 0,
  lineHeight: "35px",
  borderRadius: "20px",
};

const UserManage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useUser();
  const userRoleOptions = useUserRole();
  const { showError } = useContext(NotificationContext);
  const userTableHead = [
    { id: "fullName", label: "Full Name", align: "left" },
    { id: "email", label: "Email Address", align: "left" },
    { id: "company", label: "Company", align: "left" },
    { id: "userRole", label: "User Role", align: "left" },
    { id: "position", label: "Position", align: "left" },
    { id: "noOfProjects", label: "Projects", align: "left" },
    { id: "status", label: "Status", align: "left" },
    { id: "dateCreated", label: "Added on", align: "left", type: "date" },
    {
      id: "lastLogin",
      label: "Last Login",
      align: "left",
      type: "dateWithTime",
    },
  ];

  const [state, setState] = useState({
    originalUserData: [],
    userTableData: [],
    userRolesOptions: [],
    userRolesValue: [],
    projectsOptions: [],
    projectsValue: [],
    statusOptions: [
      { value: "Active", label: "Active" },
      { value: "Pending", label: "Pending" },
      { value: "Disable", label: "Disable" },
    ],
    statusValue: ["Active", "Pending", "Disable"],
    searchValue: "",
    showCreateUser: false,
    isLoading: true,
    isLoadingFullScreen: true,
    page: 0,
    rowsPerPage: 30,
  });
  const updateState = (field, value) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const getAllProjectsData = async () => {
    try {
      updateState("isLoadingFullScreen", true);
      const result = await getProjectsDataApi();
      if (result?.data) {
        const projectsOptions = result.data.map((item) => ({
          value: item.id,
          label: item.projectName,
        }));
        updateState("projectsOptions", projectsOptions);
        updateState(
          "projectsValue",
          projectsOptions.map((item) => item.value)
        );
      }
    } catch {
      showError("Received projects data occurred error.");
    } finally {
      updateState("isLoadingFullScreen", false);
    }
  };

  const getAllUserTableData = async () => {
    try {
      updateState("isLoading", true);
      const result = await getAllUserApi();
      if (result?.data) {
        const formattedData = result.data.map((item) => ({
          ...item,
          id: item.userId,
          fullName: item.firstName + " " + item.lastName,
          noOfProjects: item.projectIds.length,
        }));
        updateState("originalUserData", formattedData);
        updateState("userTableData", formattedData);
      }
    } catch {
      showError("Recieved users data occurred error.");
    } finally {
      updateState("isLoading", false);
    }
  };

  const searchButton = () => {
    updateState("page", 0);
    updateState("isLoading", true);
    const { originalUserData, searchValue, userRolesValue, projectsValue, statusValue } = state;
    const normalizedSearchValue = String(searchValue || "")
      .trim()
      .toLowerCase();

    let filteredData = [...originalUserData];

    if (normalizedSearchValue) {
      filteredData = filteredData.filter((project) =>
        Object.values(project).some((value) => {
          if (typeof value === "string" || typeof value === "number") {
            const normalizedValue = String(value).toLowerCase();
            return normalizedValue.includes(normalizedSearchValue);
          }
          return false;
        })
      );
    }

    if (projectsValue.length > 0) {
      filteredData = filteredData.filter((item) => {
        if (!item.projectIds || item.projectIds.length === 0) {
          return true;
        }
        return item.projectIds.some((id) => projectsValue.includes(id));
      });
    }

    if (userRolesValue.length > 0) {
      const userRolesMapping = Object.fromEntries(userRoleOptions?.map((item) => [item.value, item.label]));
      const selectedUserRole = userRolesValue.map((value) => userRolesMapping[value]);
      filteredData = filteredData.filter((item) => selectedUserRole.includes(item.userRole));
    }

    if (statusValue.length > 0) {
      filteredData = filteredData.filter((item) => statusValue.includes(item.status));
    }
    updateState("userTableData", filteredData);
    setTimeout(() => {
      updateState("isLoading", false);
    }, 100);
  };

  const resetButton = () => {
    updateState("page", 0);
    updateState("isLoading", true);
    const resetState = {
      searchValue: "",
      userRolesValue: state.userRolesOptions.map((item) => item.value),
      projectsValue: state.projectsOptions.map((item) => item.value),
      statusValue: state.statusOptions.map((item) => item.value),
      userTableData: [...state.originalUserData],
    };
    setState((prevState) => ({
      ...prevState,
      ...resetState,
    }));
    setTimeout(() => {
      updateState("isLoading", false);
    }, 100);
  };

  useEffect(() => {
    if (!userRoleOptions) return;
    const fetchData = async () => {
      updateState("userRolesOptions", userRoleOptions);
      updateState(
        "userRolesValue",
        userRoleOptions?.map((item) => item.value)
      );
      await Promise.all([getAllUserTableData(), getAllProjectsData()]);
    };

    fetchData();
  }, [userRoleOptions]);

  useEffect(() => {
    if (user && user.userRole) {
      if (user.userRole !== "System Administrator") {
        navigate(-1);
      }
    }
  }, [user]);

  return (
    <>
      <Box sx={{ minWidth: 1150 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            User Profile Management
            <img
              src="assets/icons/greytwopeople.svg"
              alt="Project"
              style={{
                width: "18px",
                height: "22px",
                marginLeft: "8px",
                verticalAlign: "text-top",
              }}
            />
          </Typography>
          <Button
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: "10px", lineHeight: "normal" }}
            onClick={() => {
              navigate("/modules/SystemAdmin");
            }}
          >
            Back
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid #D0D5DE",
            p: 2,
            mt: 1,
            height: "calc(100vh - 144px)",
            borderRadius: "10px",
          }}
        >
          <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                color="success"
                sx={{ ...buttonStyle, minWidth: 205 }}
                startIcon={<img src="assets/buttons/adduser.svg" alt="Create New User Account" />}
                onClick={() => {
                  updateState("showCreateUser", true);
                }}
              >
                Create New User Account
              </Button>
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#333",
                  fontWeight: 700,
                }}
              >
                Search
              </Typography>
              <TextField
                placeholder="Search for name, email, etc"
                variant="outlined"
                size="small"
                onChange={(e) => {
                  updateState("searchValue", e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchButton();
                  }
                }}
                value={state.searchValue}
                sx={{
                  my: 0.5,
                  width: 160,
                  ".MuiInputBase-inputSizeSmall": {
                    py: "4px!important",
                  },
                }}
              />
            </Box>
            <Box>
              <SelectComponent
                options={state.userRolesOptions}
                value={state.userRolesValue}
                onChange={(value) => {
                  updateState("userRolesValue", value);
                }}
                isMultiSelect
                selectLabel="User Role"
                selectStyle={{ width: "100%", minWidth: 160, maxWidth: 160 }}
              />
            </Box>
            <Box>
              <SelectComponent
                options={state.projectsOptions}
                value={state.projectsValue}
                onChange={(value) => {
                  updateState("projectsValue", value);
                }}
                isMultiSelect
                selectLabel="Projects"
                selectStyle={{ width: "100%", minWidth: 160, maxWidth: 160 }}
              />
            </Box>
            <Box>
              <SelectComponent
                options={state.statusOptions}
                value={state.statusValue}
                onChange={(value) => {
                  updateState("statusValue", value);
                }}
                isMultiSelect
                selectLabel="Status"
                selectStyle={{ width: "100%", minWidth: 160, maxWidth: 160 }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "end" }}>
              <Button onClick={searchButton} variant="contained" color="secondary" sx={{ borderRadius: "10px", py: 0.3, mb: 0.5 }}>
                Search
              </Button>
              <Button onClick={resetButton} variant="outlined" color="inherit" sx={{ borderRadius: "10px", py: 0.2, mb: 0.5, ml: 1 }}>
                Reset
              </Button>
            </Box>
          </Box>
          <MainTable
            paperSx={{ flex: 1 }}
            sx={tableStyle}
            columns={userTableHead}
            rows={state.userTableData}
            headerStyles={{
              default: {
                background: `linear-gradient(90deg,${theme.palette.primary.main} 0%,${theme.palette.primary.main} 70%,#32a4a0 100%)`,
                color: "#fff",
              },
            }}
            sortable
            sortableColumns={["dateCreated", "lastLogin"]}
            setSelected={(id) => {
              navigate(`/modules/UserDetails/${id[0]}`);
            }}
            showPagination
            page={state.page}
            rowsPerPage={state.rowsPerPage}
            onPageChange={(newPage) => setState((prevState) => ({ ...prevState, page: newPage }))}
            onRowsPerPageChange={(newRowsPerPage) =>
              setState((prevState) => ({
                ...prevState,
                rowsPerPage: newRowsPerPage,
              }))
            }
            isLoading={state.isLoading}
          />
        </Box>
      </Box>
      <CreateUserPopup
        open={state.showCreateUser}
        onClose={() => updateState("showCreateUser", false)}
        onSuccess={getAllUserTableData}
        userTableData={state.userTableData}
      />
      <FullScreenLoading isLoading={state.isLoadingFullScreen} />
    </>
  );
};

export default UserManage;
